import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Where one suspect stands, from investigation up to cassation: the stages
 * recorded for that person, in order, the last one marked as current. Each
 * stage shows its status and, for appeals and cassation, the court's
 * decision. Details are shown on hover.
 */
export type JourneyStage = {
  key: string;
  status?: string | null;
  decision?: string | null;
  detail?: string | null;
  date?: string | null;
  defendant_number?: number;
  kind?: "prosecutor" | "defendant";
};

const STAGE_LABELS: Record<string, string> = {
  investigation: "Investigation",
  witness_conversion: "Witness conversion",
  bail: "Bail",
  charge: "Charge",
  pre_judgement: "Pre-judgement",
  verdict: "Court verdict",
  appeal: "Appeal",
  cassation: "Cassation",
  execution: "Execution",
};

// Outcomes that end or go against the person's case, shown in grey.
const STOPPED =
  /closed|terminat|dismiss|reject|not_gulity|acquit|overturned|withdraw|revoked/i;

const humanize = (code: string) =>
  code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

const SuspectJourney: React.FC<{
  stages?: JourneyStage[];
  /** Opens the full history; the row becomes a button when given. */
  onOpen?: () => void;
}> = ({ stages, onOpen }) => {
  const { t } = useTranslation();
  if (!stages || stages.length === 0) return null;

  const text = (code?: string | null) =>
    code ? t(code, { defaultValue: humanize(code) }) : "";

  return (
    <div
      className={`mt-4 border-t border-gray-100 pt-4 ${onOpen ? "cursor-pointer rounded-b-lg hover:bg-gray-50/60" : ""}`}
      onClick={onOpen}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => onOpen && (e.key === "Enter" || e.key === " ") && onOpen()}
    >
      <p className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t("case_progress", { defaultValue: "Case progress" })}
        {onOpen && (
          <span className="normal-case tracking-normal text-[#AA232F]">
            {t("view_history", { defaultValue: "View history" })} ›
          </span>
        )}
      </p>
      <ol className="flex flex-wrap items-stretch gap-2">
        {stages.map((stage, i) => {
          const current = i === stages.length - 1;
          const stopped = STOPPED.test(`${stage.status} ${stage.decision}`);
          const label = t(`journey_${stage.key}`, {
            defaultValue: STAGE_LABELS[stage.key] || humanize(stage.key),
          });
          const tooltip = [
            stage.kind &&
              (stage.kind === "defendant"
                ? t("defendant_appeal", { defaultValue: "Defendant appeal" })
                : t("prosecutor_appeal", { defaultValue: "Prosecutor appeal" })),
            stage.defendant_number &&
              `${t("defendant", { defaultValue: "Defendant" })} ${stage.defendant_number}`,
            stage.detail,
            stage.date && new Date(stage.date).toLocaleDateString(),
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <li key={`${stage.key}-${i}`} className="flex items-center gap-2">
              <div
                title={tooltip || undefined}
                className={`min-w-[8rem] rounded-lg border px-3 py-2 ${
                  current
                    ? stopped
                      ? "border-gray-400 bg-gray-100"
                      : "border-[#AA232F] bg-[#FDEBEC]"
                    : "border-gray-200 bg-white"
                }`}
              >
                <p
                  className={`text-[11px] font-semibold uppercase tracking-wide ${
                    current && !stopped ? "text-[#AA232F]" : "text-gray-500"
                  }`}
                >
                  {label}
                </p>
                <p className="text-sm font-medium text-gray-900">{text(stage.status)}</p>
                {stage.decision && (
                  <p className="text-xs text-gray-600">
                    {t("decision", { defaultValue: "Decision" })}: {text(stage.decision)}
                  </p>
                )}
              </div>
              {!current && <span className="text-gray-300" aria-hidden>›</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default SuspectJourney;
